import { useState, useEffect } from 'react'
import api from '../api/client'
import { Loading } from '../components/ui'
import { WalletSEO } from '../hooks/useSEO'
import { useAuthStore } from '../stores/authStore'

interface WalletBalance {
  balance: number
  address: string
  currency: string
}

interface WalletStatus {
  configured: boolean
  usdcMint: string
}

export default function WalletPage() {
  const { user } = useAuthStore()
  const [balance, setBalance] = useState<WalletBalance | null>(null)
  const [status, setStatus] = useState<WalletStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLinking, setIsLinking] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [copied, setCopied] = useState(false)

  // Send form
  const [sendTo, setSendTo] = useState('')
  const [sendAmount, setSendAmount] = useState('')
  const [sendType, setSendType] = useState<'address' | 'email'>('email')

  // Get wallet address from balance or user's bot
  const walletAddress = balance?.address || user?.bot?.walletAddress || ''

  const copyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress)
      setCopied(true)
    }
  }

  // Cleanup timeout for copy indicator
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  useEffect(() => {
    fetchWalletData()
  }, [])

  const fetchWalletData = async () => {
    setIsLoading(true)
    try {
      const [statusData, balanceData] = await Promise.all([
        api.get<WalletStatus>('/wallet/status').catch((err) => {
          console.error('Status fetch error:', err)
          return null
        }),
        api.get<WalletBalance>('/wallet/balance').catch((err) => {
          console.error('Balance fetch error:', err)
          return null
        }),
      ])
      if (!statusData) {
        setError('Failed to load wallet status')
        return
      }
      setStatus(statusData)
      setBalance(balanceData)
    } catch (err) {
      console.error('Wallet fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load wallet')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLinkWallet = async () => {
    setIsLinking(true)
    setError('')
    setSuccess('')
    try {
      const result = await api.post<{ success: boolean; message: string }>('/wallet/link')
      setSuccess(result.message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link wallet')
    } finally {
      setIsLinking(false)
    }
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSending(true)
    setError('')
    setSuccess('')

    const amount = parseFloat(sendAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount')
      setIsSending(false)
      return
    }

    try {
      let result
      if (sendType === 'email') {
        result = await api.post<{ success: boolean; message: string; txHash?: string }>('/wallet/transfer', {
          toEmail: sendTo,
          amount,
        })
      } else {
        result = await api.post<{ success: boolean; message: string; txHash?: string }>('/wallet/send', {
          toAddress: sendTo,
          amount,
        })
      }
      setSuccess(result.message)
      setSendTo('')
      setSendAmount('')
      fetchWalletData() // Refresh balance
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send USDC')
    } finally {
      setIsSending(false)
    }
  }

  if (isLoading) {
    return <Loading message="Loading wallet..." />
  }

  if (!status?.configured) {
    return (
      <div>
        <div className="page-header">
          <h1>Wallet</h1>
        </div>
        <div className="alert" style={{ background: 'var(--bg-secondary)' }}>
          Wallet service is not configured. Please contact support.
        </div>
      </div>
    )
  }

  return (
    <div>
      <WalletSEO />
      <div className="page-header">
        <h1>Wallet</h1>
        <p className="text-secondary">Manage your USDC on Solana</p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}

      {/* Balance Card */}
      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>Balance</h2>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '36px', fontWeight: 700 }}>
            ${balance?.balance?.toFixed(2) || '0.00'}
          </span>
          <span className="text-secondary">USDC</span>
        </div>

        {/* Wallet Address */}
        {walletAddress ? (
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '1rem',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span className="text-secondary" style={{ fontSize: '13px', fontWeight: 500 }}>Wallet Address</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={copyAddress}
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              wordBreak: 'break-all',
              color: 'var(--text-primary)'
            }}>
              {walletAddress}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-secondary" style={{ marginBottom: '1rem' }}>
              Link your wallet to receive and send USDC.
            </p>
            <button
              className="btn btn-primary"
              onClick={handleLinkWallet}
              disabled={isLinking}
            >
              {isLinking ? 'Sending Link...' : 'Link Wallet'}
            </button>
          </div>
        )}
      </div>

      {/* Send Card */}
      {walletAddress && (
        <div className="card-static" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Send USDC</h2>
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label className="label">Send To</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className={`btn btn-sm ${sendType === 'email' ? 'btn-primary' : ''}`}
                  onClick={() => setSendType('email')}
                >
                  Email
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${sendType === 'address' ? 'btn-primary' : ''}`}
                  onClick={() => setSendType('address')}
                >
                  Address
                </button>
              </div>
              <input
                type={sendType === 'email' ? 'email' : 'text'}
                className="input"
                placeholder={sendType === 'email' ? 'recipient@example.com' : 'Solana wallet address'}
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Amount (USDC)</label>
              <input
                type="number"
                className="input"
                placeholder="0.00"
                step="0.01"
                min="0.01"
                value={sendAmount}
                onChange={(e) => setSendAmount(e.target.value)}
                required
              />
              <p className="form-hint">Available: ${balance?.balance?.toFixed(2) || '0.00'} USDC</p>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSending}
            >
              {isSending ? 'Sending...' : 'Send USDC'}
            </button>
          </form>
        </div>
      )}

      {/* Transaction History Link */}
      {walletAddress && (
        <div className="card-static">
          <h2 style={{ marginBottom: '1rem' }}>Transaction History</h2>
          <p className="text-secondary" style={{ marginBottom: '1rem' }}>
            View all your USDC transactions on the Solana blockchain.
          </p>
          <a
            href={`https://solscan.io/account/${walletAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            View on Solscan
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        </div>
      )}
    </div>
  )
}
